import {
  createEffect,
  createMemo,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import type {
  FloorPlan,
  PlanBasemap,
  PlanMark,
  PlanOrientation,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { t } from "../i18n.ts";
import { pathTo, siteTree } from "../context.ts";
import { subtreeOf } from "../tree.ts";
import { geoOf, groupMarks, planUnits, type PlanUnit } from "../plans.ts";
import { Icon } from "../icons.tsx";
import { mediumColor } from "./Media.tsx";
import { ErrorBox } from "./ErrorBox.tsx";
import { type MapView, PlanMapView } from "./PlanMap.tsx";
import { useAction } from "./ui.tsx";

const STEPS = ["file", "scale", "points"] as const;
const ORIENTATIONS: PlanOrientation[] = [
  "north-up",
  "north-right",
  "north-down",
  "north-left",
  "none",
];
const ACCEPT = ["image/png", "image/jpeg", "image/webp"];
// Without a position of the place the map opens over Poland.
const COUNTRY: MapView = { latitude: 52, longitude: 19.4, zoom: 6 };
const PLACE_ZOOM = 17;

type Background = "image" | "map";

/**
 * Adds or edits the floor plan of a place in three steps (prototype "Rzut" wizard): the background — an image of the
 * plan, or a map (streets or aerial photos) for places outdoors — its scale and orientation, and where the place's
 * points are on it. Nothing is saved before the last step.
 */
export function PlanWizard(props: {
  nodeId: string;
  plan: FloorPlan | null;
  /** Object URL of the current image, when the place has a plan. */
  image: string | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [step, setStep] = createSignal(0);
  const [file, setFile] = createSignal<File>();
  const [preview, setPreview] = createSignal<string | undefined>(props.image);
  const [width, setWidth] = createSignal(
    props.plan?.widthM == null ? "" : String(props.plan.widthM),
  );
  const [orientation, setOrientation] = createSignal<PlanOrientation>(
    props.plan?.orientation ?? "north-up",
  );
  const [background, setBackground] = createSignal<Background>(
    props.plan?.map ? "map" : "image",
  );
  const [basemap, setBasemap] = createSignal<PlanBasemap>(
    props.plan?.map?.basemap ?? "orto",
  );
  const startView = (): MapView => {
    if (props.plan?.map) {
      return props.plan.map;
    }
    const geo = geoOf(siteTree()?.nodes ?? [], props.nodeId);
    return geo
      ? { latitude: geo.latitude, longitude: geo.longitude, zoom: PLACE_ZOOM }
      : COUNTRY;
  };
  const [view, setView] = createSignal<MapView>(startView());
  // Positions on an image (fractions) and on a map (longitude, latitude) are kept apart while switching.
  const [imageMarks, setImageMarks] = createSignal<PlanMark[]>(
    props.plan?.map ? [] : (props.plan?.marks ?? []),
  );
  const [mapMarks, setMapMarks] = createSignal<PlanMark[]>(
    props.plan?.map ? props.plan.marks : [],
  );
  const marks = () => (background() === "map" ? mapMarks() : imageMarks());
  const setMarks = (next: PlanMark[]) =>
    background() === "map" ? setMapMarks(next) : setImageMarks(next);
  const [current, setCurrent] = createSignal<string>();
  const [problem, setProblem] = createSignal<string>();
  const [over, setOver] = createSignal(false);
  const [run, error, busy] = useAction();
  let pick: HTMLDivElement | undefined;

  const path = () =>
    pathTo(siteTree()?.nodes ?? [], props.nodeId)
      .map((n) => n.name)
      .join(" / ");
  const points = createMemo(() => {
    const inside = new Set(subtreeOf(siteTree()?.nodes ?? [], props.nodeId));
    return (siteTree()?.points ?? [])
      .filter((p) => inside.has(p.nodeId))
      .sort((a, b) => a.name.localeCompare(b.name));
  });
  // What is placed with one click: a device with all its points (phases and their sum), or a point on its own.
  const units = createMemo(() => planUnits(points()));
  const unit = (key: string | undefined) => units().find((u) => u.key === key);
  const unitOfPoint = (pointId: string) =>
    units().find((u) => u.pointIds.includes(pointId));
  const placed = (u: PlanUnit) =>
    u.pointIds.some((id) => marks().some((m) => m.pointId === id));
  const nextUnplaced = () => units().find((u) => !placed(u))?.key;
  createEffect(() => {
    if (current() === undefined) {
      setCurrent(nextUnplaced() ?? units()[0]?.key);
    }
  });
  const groups = createMemo(() => groupMarks(marks(), points()));
  /** Puts these points at one spot; other points keep theirs. */
  const putAt = (pointIds: string[], x: number, y: number) =>
    setMarks([
      ...marks().filter((m) => !pointIds.includes(m.pointId)),
      ...pointIds.map((pointId) => ({ pointId, x, y })),
    ]);
  const isCurrent = (pointIds: string[]) =>
    unit(current())?.pointIds.includes(pointIds[0]!) ?? false;

  const choose = (f: File | undefined) => {
    if (!f) {
      return;
    }
    if (!ACCEPT.includes(f.type)) {
      setProblem(t("plans.wizard.badFile"));
      return;
    }
    setProblem(undefined);
    const old = file() ? preview() : undefined;
    if (old) {
      URL.revokeObjectURL(old);
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  onCleanup(() => {
    const url = preview();
    if (file() && url) {
      URL.revokeObjectURL(url);
    }
  });

  const metres = () => {
    const s = width().trim().replace(",", ".");
    return s === "" ? null : Number(s);
  };
  const check = (): string | null => {
    switch (STEPS[step()]) {
      case "file":
        return background() === "map" || preview()
          ? null
          : t("plans.wizard.needFile");
      case "scale": {
        if (background() === "map") {
          return null;
        }
        const m = metres();
        return m === null || (Number.isFinite(m) && m > 0 && m <= 10_000)
          ? null
          : t("plans.wizard.badWidth");
      }
      default:
        return null;
    }
  };

  // Where on the image, as fractions; marks can be dragged once placed.
  const at = (e: PointerEvent) => {
    const r = pick!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };
  const [dragging, setDragging] = createSignal<string>();
  const place = (e: PointerEvent) => {
    const u = unit(current());
    if (!u || dragging()) {
      return;
    }
    const p = at(e);
    putAt(u.pointIds, p.x, p.y);
    setCurrent(nextUnplaced() ?? u.key);
  };
  // Dragging moves every point of the marker together; the marker is followed by its first point, as its key
  // changes with its position.
  const onMove = (e: PointerEvent) => {
    const first = dragging();
    const g = groups().find((x) => x.pointIds.includes(first ?? ""));
    if (g) {
      const p = at(e);
      putAt(g.pointIds, p.x, p.y);
    }
  };

  const placeOnMap = (latitude: number, longitude: number) => {
    const u = unit(current());
    if (u) {
      putAt(u.pointIds, longitude, latitude);
      setCurrent(nextUnplaced() ?? u.key);
    }
  };
  const mapMarkers = () =>
    groups().map((g) => {
      const p = points().find((x) => x.id === g.pointIds[0]);
      return {
        pointId: g.key,
        latitude: g.y,
        longitude: g.x,
        color: mediumColor(p?.medium ?? "el"),
        title:
          g.pointIds.length > 1 ? `${g.title} (${g.pointIds.length})` : g.title,
        selected: isCurrent(g.pointIds),
      };
    });

  const save = () =>
    void run(async () => {
      if (background() === "map") {
        await api.plans.setMap(props.nodeId, {
          basemap: basemap(),
          ...view(),
        });
      } else {
        const f = file();
        if (f) {
          await api.plans.setImage(props.nodeId, f);
        }
        await api.plans.setScale(props.nodeId, metres(), orientation());
      }
      await api.plans.setMarks(props.nodeId, marks());
      props.onSaved();
      props.onClose();
    });
  const next = () => {
    const p = check();
    setProblem(p ?? undefined);
    if (p) {
      return;
    }
    if (step() === STEPS.length - 1) {
      save();
    } else {
      setStep(step() + 1);
    }
  };

  const onKey = (e: KeyboardEvent) => e.key === "Escape" && props.onClose();
  onMount(() => document.addEventListener("keydown", onKey));
  onCleanup(() => document.removeEventListener("keydown", onKey));

  return (
    <div
      class="ovl"
      onMouseDown={(e) => e.target === e.currentTarget && props.onClose()}
    >
      <div
        class="dlg"
        role="dialog"
        aria-modal="true"
        aria-label={t("plans.wizard.title", { name: path() })}
        style={{ width: "min(820px, 100%)" }}
      >
        <div class="dlg-h">
          <div>
            <div class="eyebrow">
              {t("plans.wizard.eyebrow")} ·{" "}
              {t("events.wizard.stepOf", {
                step: step() + 1,
                total: STEPS.length,
              })}
            </div>
            <h2>{t("plans.wizard.title", { name: path() })}</h2>
          </div>
          <button
            type="button"
            class="btn ghost"
            style={{ "margin-left": "auto" }}
            aria-label={t("common.close")}
            onClick={() => props.onClose()}
          >
            <Icon name="x" />
          </button>
        </div>
        <ol class="wzbar">
          <For each={STEPS}>
            {(s, i) => (
              <li class={i() < step() ? "done" : i() === step() ? "cur" : ""}>
                <button
                  type="button"
                  disabled={i() > step()}
                  onClick={() => setStep(i())}
                >
                  {i() < step() ? "✓ " : ""}
                  {t(`plans.wizard.step.${s}`)}
                </button>
              </li>
            )}
          </For>
        </ol>
        <div class="dlg-b" style={{ display: "grid", gap: "12px" }}>
          <Show when={STEPS[step()] === "file"}>
            <div class="field">
              <span class="eyebrow">{t("plans.wizard.place")}</span>
              <div>{path()}</div>
            </div>
            <div
              class="seg"
              role="group"
              aria-label={t("plans.wizard.background")}
            >
              <For each={["image", "map"] as const}>
                {(b) => (
                  <button
                    type="button"
                    aria-pressed={background() === b}
                    onClick={() => {
                      setBackground(b);
                      setCurrent(undefined);
                    }}
                  >
                    <Icon name={b === "image" ? "floor" : "map"} />{" "}
                    {t(`plans.wizard.backgrounds.${b}`)}
                  </button>
                )}
              </For>
            </div>
            <Show when={background() === "map"}>
              <div class="field" style={{ "max-width": "420px" }}>
                <label for="plB">{t("plans.wizard.basemap")}</label>
                <select
                  id="plB"
                  value={basemap()}
                  onChange={(e) =>
                    setBasemap(e.currentTarget.value as PlanBasemap)
                  }
                >
                  <option value="orto">{t("plans.map.basemap.orto")}</option>
                  <option value="osm">{t("plans.map.basemap.osm")}</option>
                </select>
              </div>
              <PlanMapView
                basemap={basemap()}
                view={view()}
                marks={mapMarkers()}
                height="300px"
                label={t("plans.wizard.backgrounds.map")}
                onView={setView}
              />
              <p class="hint" style={{ margin: 0 }}>
                {t("plans.wizard.mapHint")}
              </p>
              <Show when={props.plan && !props.plan.map}>
                <p
                  class="chip warn"
                  style={{ margin: 0, "justify-self": "start" }}
                >
                  {t("plans.wizard.switchDropsMarks")}
                </p>
              </Show>
            </Show>
            <Show when={background() === "image"}>
              <Show when={props.plan?.map}>
                <p
                  class="chip warn"
                  style={{ margin: 0, "justify-self": "start" }}
                >
                  {t("plans.wizard.switchDropsMarks")}
                </p>
              </Show>
              <label
                class={`dropzone${over() ? " over" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOver(true);
                }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver(false);
                  choose(e.dataTransfer?.files[0]);
                }}
              >
                <Icon name="plus" />
                <span>
                  <b style={{ "font-weight": 500 }}>
                    {preview()
                      ? t("plans.wizard.change")
                      : t("plans.wizard.choose")}
                  </b>{" "}
                  — {t("plans.wizard.formats")}
                </span>
                <input
                  type="file"
                  accept={ACCEPT.join(",")}
                  hidden
                  onChange={(e) => choose(e.currentTarget.files?.[0])}
                />
              </label>
              <p class="hint" style={{ margin: 0, opacity: 0.6 }}>
                {t("plans.wizard.convertSoon")}
              </p>
              <Show when={preview()}>
                <img
                  src={preview()}
                  alt={t("plans.wizard.preview")}
                  style={{
                    "max-width": "100%",
                    "max-height": "220px",
                    "border-radius": "6px",
                    border: "1px solid var(--line)",
                    "justify-self": "start",
                  }}
                />
              </Show>
            </Show>
          </Show>
          <Show when={STEPS[step()] === "scale" && background() === "map"}>
            <p class="hint" style={{ margin: 0 }}>
              {t("plans.wizard.mapScale")}
            </p>
          </Show>
          <Show when={STEPS[step()] === "scale" && background() === "image"}>
            <div class="grid g-2e" style={{ gap: "10px" }}>
              <div class="field">
                <label for="plW">{t("plans.wizard.width")}</label>
                <input
                  id="plW"
                  inputmode="decimal"
                  value={width()}
                  placeholder="48"
                  onInput={(e) => setWidth(e.currentTarget.value)}
                />
              </div>
              <div class="field">
                <label for="plO">{t("plans.wizard.orientation")}</label>
                <select
                  id="plO"
                  value={orientation()}
                  onChange={(e) =>
                    setOrientation(e.currentTarget.value as PlanOrientation)
                  }
                >
                  <For each={ORIENTATIONS}>
                    {(o) => (
                      <option value={o}>{t(`plans.orientation.${o}`)}</option>
                    )}
                  </For>
                </select>
              </div>
            </div>
            <p class="hint" style={{ margin: 0 }}>
              {t("plans.wizard.scaleHint")}
            </p>
          </Show>
          <Show when={STEPS[step()] === "points"}>
            <Show
              when={points().length > 0}
              fallback={<p class="muted">{t("plans.wizard.noPoints")}</p>}
            >
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  "align-items": "end",
                  "flex-wrap": "wrap",
                }}
              >
                <div class="field" style={{ flex: "1 1 280px" }}>
                  <label for="plP">{t("plans.wizard.pick")}</label>
                  <select
                    id="plP"
                    value={current() ?? ""}
                    onChange={(e) => setCurrent(e.currentTarget.value)}
                  >
                    <For each={units()}>
                      {(u) => (
                        <option value={u.key}>
                          {u.pointIds.length > 1
                            ? t("plans.wizard.devicePoints", {
                                name: u.title,
                                count: u.pointIds.length,
                              })
                            : u.title}
                          {placed(u) ? " ✓" : ""}
                        </option>
                      )}
                    </For>
                  </select>
                </div>
                <Show when={unit(current()) && placed(unit(current())!)}>
                  <button
                    type="button"
                    class="btn sm"
                    onClick={() => {
                      const ids = unit(current())!.pointIds;
                      setMarks(marks().filter((m) => !ids.includes(m.pointId)));
                    }}
                  >
                    {t("plans.wizard.unplace")}
                  </button>
                </Show>
              </div>
            </Show>
            <Show when={background() === "map"}>
              <PlanMapView
                basemap={basemap()}
                view={view()}
                marks={mapMarkers()}
                height="420px"
                label={t("plans.wizard.backgrounds.map")}
                onView={setView}
                onMapClick={placeOnMap}
                onMarkClick={(key) => {
                  const g = groups().find((x) => x.key === key);
                  const u = g && unitOfPoint(g.pointIds[0]!);
                  if (u) {
                    setCurrent(u.key);
                  }
                }}
                onMarkMoved={(key, latitude, longitude) => {
                  const g = groups().find((x) => x.key === key);
                  if (g) {
                    putAt(g.pointIds, longitude, latitude);
                  }
                }}
              />
            </Show>
            <Show when={background() === "image"}>
              <div
                ref={pick}
                class="planpick"
                style={{
                  position: "relative",
                  cursor: "crosshair",
                  "touch-action": "none",
                }}
                onPointerUp={(e) => {
                  if (dragging()) {
                    setDragging(undefined);
                  } else {
                    place(e);
                  }
                }}
                onPointerMove={onMove}
                onPointerLeave={() => setDragging(undefined)}
              >
                <img
                  src={preview()}
                  alt={t("plans.wizard.preview")}
                  draggable={false}
                  style={{
                    width: "100%",
                    display: "block",
                    "border-radius": "6px",
                  }}
                />
                <For each={groups()}>
                  {(g) => {
                    const p = () =>
                      points().find((x) => x.id === g.pointIds[0]);
                    return (
                      <span
                        class="pmk"
                        title={
                          g.pointIds.length > 1
                            ? `${g.title} (${g.pointIds.length})`
                            : g.title
                        }
                        style={{
                          left: `${g.x * 100}%`,
                          top: `${g.y * 100}%`,
                          background: mediumColor(p()?.medium ?? "el"),
                          cursor: "move",
                          outline: isCurrent(g.pointIds)
                            ? "2px solid var(--ink)"
                            : undefined,
                        }}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          const u = unitOfPoint(g.pointIds[0]!);
                          if (u) {
                            setCurrent(u.key);
                          }
                          setDragging(g.pointIds[0]);
                        }}
                      />
                    );
                  }}
                </For>
              </div>
            </Show>
            <Show when={units().some((u) => u.pointIds.length > 1)}>
              <p class="hint" style={{ margin: 0 }}>
                {t("plans.wizard.deviceHint")}
              </p>
            </Show>
            <p class="hint" style={{ margin: 0 }}>
              {t("plans.wizard.placed", {
                placed: units().filter(placed).length,
                total: units().length,
              })}
            </p>
          </Show>
          <Show when={problem()}>
            <p
              class="chip warn"
              role="alert"
              style={{ margin: 0, "justify-self": "start" }}
            >
              {problem()}
            </p>
          </Show>
          <Show when={error()}>
            <ErrorBox error={error()} />
          </Show>
        </div>
        <div class="dlg-f">
          <Show
            when={step() > 0}
            fallback={
              <button type="button" class="btn" onClick={() => props.onClose()}>
                {t("common.cancel")}
              </button>
            }
          >
            <button
              type="button"
              class="btn"
              onClick={() => setStep(step() - 1)}
            >
              <Icon name="chevl" />
              {t("common.back")}
            </button>
          </Show>
          <button
            type="button"
            class="btn primary"
            disabled={busy()}
            onClick={next}
          >
            {step() === STEPS.length - 1
              ? t("plans.wizard.save")
              : t("common.next")}
            <Show when={step() < STEPS.length - 1}>
              <Icon name="chev" />
            </Show>
          </button>
        </div>
      </div>
    </div>
  );
}
